import { removeHash } from '../functionLibrary'
import type {
	IManifestPublicElement,
	ResolvedBuilderWidgetDataItem,
	ResolvedInnerElementsDataItem,
	StyleDataItem,
	WixNamespace, ElementProps } from '@wix/thunderbolt-becky-types'
import type { Experiments, BuilderComponentsData } from '@wix/thunderbolt-symbols'
import _ from 'lodash'
import { extractComponentData, getBuilderWidgetClass, REF_DELIMITER } from '@wix/thunderbolt-commons'

// Constants for presets props functionality
const PRESETS_WRAPPER_BASE_CLASS = 'wix-presets-wrapper'
const ROOT_PATH_PREFIX = 'root/'
const ROOT_PATH = 'root'

const getInnerElementsOverridesMaps = (
	compInnerElementsDataItem: ResolvedBuilderWidgetDataItem
): {
	innerElementsOverrides: Record<string, any>
	dataQueries: Array<string>
	styleQueries: Array<string>
} => {
	const innerElementsOverrides: any = {}
	const dataQueries: Array<string> = []
	const styleQueries: Array<string> = []
	let currInnerElement = innerElementsOverrides

	const recurInnerElements = (innerElementsDataItem: ResolvedBuilderWidgetDataItem, compPath: string) => {
		const overridesMap = innerElementsDataItem?.innerElements || {}

		_.keys(overridesMap).forEach((nickname) => {
			const currNicknamePath = compPath.length ? `${compPath}/${nickname}` : nickname
			currInnerElement[nickname] = {}

			const currInnerElementData: ResolvedInnerElementsDataItem = _.get(
				overridesMap,
				nickname
			)

			if (currInnerElementData.dataQuery) {
				const dataQuery = removeHash(currInnerElementData.dataQuery)!
				currInnerElement[nickname].dataQuery = dataQuery
				dataQueries.push(dataQuery)
			}

			if (currInnerElementData.styleId) {
				const styleId = removeHash(currInnerElementData.styleId)!
				currInnerElement[nickname].styleId = styleId
				styleQueries.push(styleId)
			}

			const slotsQuery = currInnerElementData?.slotsQuery
			if (slotsQuery) {
				currInnerElement[nickname].slotsQuery = slotsQuery
			}

			if (currInnerElementData.innerElementsQuery) {
				const saveParentCompRef = currInnerElement
				currInnerElement = currInnerElement[nickname]
				recurInnerElements(currInnerElementData.innerElementsQuery, currNicknamePath)
				currInnerElement = saveParentCompRef
			}
		})
	}
	if (compInnerElementsDataItem?.innerElements) {
		recurInnerElements(compInnerElementsDataItem, '')
	}

	return { innerElementsOverrides, dataQueries, styleQueries }
}

const isElementRemoved = (style: any) => {
	if (style.type !== 'RefArray') {
		return style?.display === 'none'
	}
	return _.every(style.values, (value) => value?.display === 'none' || value?.to?.display === 'none')
}

const getElementRemovalState = (removedItems: Array<string>, innerElement: boolean = false) => {
	const removedItemsMap = Object.fromEntries(removedItems.map((item) => [item, 'REMOVED']))
	return removedItems.length > 0
		? innerElement
			? { wix: { elementsRemovalState: removedItemsMap } }
			: { elementsRemovalState: removedItemsMap }
		: {}
}

/**
 * Resolves inner elements data overrides by replacing string references with actual data items.
 * This function modifies the data to be the actual props expected to pass to the component, using the 'elementProps' notation and excluding internal properties.
 *
 * @param {Record<string, any>} innerElementsDataOverrides - The overrides for inner elements.
 * @param {Record<string, any>} dataItems - The data items to use for resolving references.
 * @param {Record<string, StyleDataItem>} styleItems - The style items to use for resolving references.
 * @param {Record<string, { slots: Record<string, string> }>} slotsData - The slots data.
 * @param {string} compId - The component ID.
 * @param {IManifestPublicElement['reducedComponentModel']} reducedManifest - The reduced manifest.
 * @param {BuilderComponentsData} builderComponentsData - The builder components data.
 * @param {string} builderType - The builder type.
 * @param {IManifestPublicElement['reducedComponentModel']} reducedManifest - The reduced manifest.
 * @returns {Record<string, any>} - The resolved inner elements data props.
 */
const resolveInnerElementsDataProps = (
	innerElementsDataOverrides: Record<string, any>,
	dataItems: Record<string, any>,
	styleItems: Record<string, StyleDataItem>,
	slotsData: Record<string, { slots: Record<string, string> }>,
	compId: string,
	reducedManifest: IManifestPublicElement['reducedComponentModel']
) => {
	const resolveElementPropsItemOverrides = (
		key: string,
		val: any,
		dataTarget: Record<string, any>,
		path: string = ''
	) => {
		const { dataQuery, styleId, slotsQuery, ...elementProps } = val

		const removedItems = []

		let data: Record<string, any> = {}

		if (dataItems[dataQuery]?.data) {
			data = extractComponentData(dataItems[dataQuery])
		}
		if (styleId && isElementRemoved(styleItems[styleId])) {
			removedItems.push(key)
		}

		const slotData = slotsData?.[slotsQuery]?.slots
		const currentPath = path.length > 0 ? `${path}/${key}` : key
		if (slotData) {
			data = { ...data, ...resolveElementSlots(slotData, compId, reducedManifest, currentPath) }
		}

		if (Object.keys(elementProps).length > 0) {
			data.elementProps = {}

			Object.entries(elementProps).forEach(([itemKey, itemVal]) => {
				const InnerRemovedItems = resolveElementPropsItemOverrides(itemKey, itemVal, data.elementProps, currentPath)
				data = {
					...data,
					...getElementRemovalState(InnerRemovedItems, true),
				}
			})
			if (Object.keys(data.elementProps).length === 0) {
				delete data.elementProps
			}
		}
		if (Object.keys(data).length > 0) {
			dataTarget[key] = data
		}

		return removedItems
	}

	const dataOverrides = {
		elementProps: {},
	}
	const removedItems: Array<string> = []

	Object.entries(innerElementsDataOverrides).forEach(([innerElementId, innerElementData]: [string, any]) => {
		removedItems.push(
			...resolveElementPropsItemOverrides(innerElementId, innerElementData, dataOverrides.elementProps)
		)
	})

	return {
		...dataOverrides,
		...getElementRemovalState(removedItems),
	}
}

/**
 * Resolves inner elements properties by replacing string references with actual data items.
 * This function merges the results from resolveInnerElementsDataProps and resolvePresetsProps
 * to provide complete props for builder components.
 *
 * @param {Record<string, any>} innerElementsDataOverrides - The overrides for inner elements.
 * @param {Record<string, any>} dataItems - The data items to use for resolving references.
 * @param {Record<string, StyleDataItem>} styleItems - The style items to use for resolving references.
 * @param {Record<string, { slots: Record<string, string> }>} slotsData - The slots data.
 * @param {string} compId - The component ID.
 * @param {IManifestPublicElement['reducedComponentModel']} reducedManifest - The reduced manifest.
 * @param {Experiments} experiments - The experiments object for feature flagging.
 * @param {BuilderComponentsData} builderComponentsData - The builder components data.
 * @param {string} builderType - The builder type.
 * @returns {Record<string, any>} - The resolved and merged inner elements props.
 */
const resolveInnerElementsProps = (
	innerElementsDataOverrides: Record<string, any>,
	dataItems: Record<string, any>,
	styleItems: Record<string, StyleDataItem>,
	slotsData: Record<string, { slots: Record<string, string> }>,
	compId: string,
	reducedManifest: IManifestPublicElement['reducedComponentModel'],
	builderComponentsData: BuilderComponentsData,
	builderType: string,
	experiments: Experiments
) => {
	// Get the data-driven props from the original logic
	const dataProps = resolveInnerElementsDataProps(
		innerElementsDataOverrides,
		dataItems,
		styleItems,
		slotsData,
		compId,
		reducedManifest
	)

	// Only generate and merge recursive wix props if builderType is provided AND experiment is enabled
	if (builderType && reducedManifest && experiments?.['specs.thunderbolt.supportExternalDependenciesCss']) {
		const wixProps = resolvePresetsProps(reducedManifest, builderComponentsData, builderType, compId)
		return _.merge({}, dataProps, wixProps)
	}

	// Return only the data props when no builderType is provided or experiment is disabled (backward compatibility)
	return dataProps
}

// Key is either compId or innerElementKey
const getPresetsWrapperProps = (widgetId: string, key: string): ElementProps['wix']['presetsWrapperProps'] => {
	return {
		className: `${PRESETS_WRAPPER_BASE_CLASS} ${getBuilderWidgetClass(widgetId)} ${key}-presets-wrapper`,
	}
}

/**
 * Builds nested presets structure from element data paths using a simple recursive approach
 * @param elementData - Element data containing all paths and their type information
 * @param builderType - Builder type for generating CSS classes
 * @param rootPath - The root path to use as base
 * @returns Nested structure with preset wrapper props only for elements with types
 */
const buildNestedPresetsStructure = (elementData: Record<string, any>, builderComponentsData: BuilderComponentsData, rootPath: string): Record<string, ElementProps> => {
	const result: Record<string, ElementProps> = {}

	// Get all paths except root, sorted by depth
	const allPaths = Object.keys(elementData)
		.filter((path) => path !== rootPath && path.startsWith(ROOT_PATH_PREFIX))
		.sort((a, b) => {
			const aDepth = a.split('/').length
			const bDepth = b.split('/').length
			return aDepth - bDepth
		})

	// Build nested structure
	for (const fullPath of allPaths) {
		const relativePath = fullPath.substring(ROOT_PATH_PREFIX.length)
		const nestedElements = relativePath.split('/')

		let currentLevel = result

		// Navigate/create the nested structure
		for (let i = 0; i < nestedElements.length; i++) {
			const element = nestedElements[i]
			const isLeafElement = i === nestedElements.length - 1

			if (!currentLevel[element]) {
				const elementProps: ElementProps = {
					wix: {},
					elementProps: {}
				}

				// Only add presets wrapper props if this specific path has a type
				if (isLeafElement && elementData[fullPath]?.type) {
					const widgetId = builderComponentsData?.components[elementData[fullPath]?.type]?.componentId
					// Use the element key directly for CSS class (without root prefix)
					elementProps.wix.presetsWrapperProps = getPresetsWrapperProps(widgetId, element)
				}

				currentLevel[element] = elementProps
			}

			// Move to the next level if not the leaf element
			if (!isLeafElement) {
				currentLevel = currentLevel[element].elementProps as Record<string, ElementProps>
			}
		}
	}

	return result
}

const resolvePresetsProps = (
	reducedManifest: IManifestPublicElement['reducedComponentModel'],
	builderComponentsData: BuilderComponentsData,
	builderType: string,
	compId: string
): ElementProps => {
	const createBaseProps = (builderType: string): ElementProps => {
		const widgetId = builderComponentsData?.components[builderType]?.componentId
		return {
			wix: { presetsWrapperProps: getPresetsWrapperProps(widgetId, compId) },
			elementProps: {}
		}
	}

	// Handle cases where we can't build nested structure
	if (!reducedManifest?.elementData) {
		return createBaseProps(builderType)
	}

	const { elementData } = reducedManifest

	const hasPresets = Object.keys(elementData?.root?.presets || {}).length > 0
	if (!hasPresets) {
		return {
			wix: { presetsWrapperProps: undefined },
			elementProps: {}
		}
	}

	// Find root path (shortest path in elementData)
	const allPaths = Object.keys(elementData).sort((a, b) => a.length - b.length)
	const rootPath = allPaths[0] || ''

	// Build nested structure from the element data
	const nestedElementProps = buildNestedPresetsStructure(elementData, builderComponentsData, rootPath)
	const baseProps = createBaseProps(builderType)

	return {
		...baseProps,
		elementProps: nestedElementProps
	}
}

function getRefPrefix(compId: string) {
	return compId.substring(0, compId.lastIndexOf(REF_DELIMITER) + REF_DELIMITER.length)
}

function resolveElementCompId(compId: string, slotCompId: string) {
	const isRefComponent = compId.includes(REF_DELIMITER)
	const prefix = isRefComponent ? getRefPrefix(compId) : ''
	return `${prefix}${slotCompId}`
}

function resolveElementSlots(
	slotData: Record<string, string>,
	compId: string,
	reducedManifest: IManifestPublicElement['reducedComponentModel'],
	currentPath: string
) {
	return _.mapValues(slotData, (slotCompId, containerName) => {
		const currentPathWithRootPrefix = `${ROOT_PATH_PREFIX}${currentPath}`
		const containerData = reducedManifest?.elementData?.[currentPathWithRootPrefix]?.containersData?.[containerName]

		return { compId: resolveElementCompId(compId, slotCompId), ...containerData }
	})
}

export { getInnerElementsOverridesMaps, resolveInnerElementsProps }
